import React, { memo, useState, useEffect, useRef } from 'react';
import { Row, Badge, Spinner, Col, Card } from 'react-bootstrap';
import { getStillageAuditDetails } from 'services/stillageDashboard';
import PropTypes from 'prop-types';
import Avatar from 'components/common/Avatar';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons';

const StillageAudit = ({ showModal, setShowModal, stillageNo }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const controllerRef = useRef(null);

    useEffect(() => {
        if (!showModal || !stillageNo) {
            controllerRef.current?.abort();
            setData([]);
            return;
        }

        setLoading(true);
        controllerRef.current = new AbortController();
        getStillageAuditDetails(stillageNo, controllerRef.current)
            .then(res => setData(res))
            .catch(error => {
                setData(null);
                setShowModal(false);
                if (error.name !== 'CanceledError')
                    toast.error('Error fetching history data:', error);
            })
            .finally(() => setLoading(false));

        return () => {
            controllerRef.current?.abort();
            controllerRef.current = null;
        };
    }, [showModal, stillageNo]);

    if (loading)
        return (
            <div className='justify-content-center d-flex w-100 my-5'>
                <Spinner animation="border" role="status" variant='primary' />
            </div>
        );

    if (!data.length && !loading)
        return (
            <div className='h-100 text-center align-middle my-4'>No data</div>
        );

    return (
        <Row className='p-3'>
            {
                data.map((change, idx) => {

                    return (
                        <Col xs={12} key={`${change.userId}_${idx}`} className='position-relative'>
                            <div
                                className='bg-400 h-100'
                                style={{
                                    top: 0, left: 15, position: 'absolute', width: '1px'
                                }}
                            ></div>
                            <div style={{ top: 0, left: 0, position: 'absolute', zIndex: 1 }}>
                                <Avatar name={change.userName} />
                            </div>
                            <Card bg='light' text='dark' className='mt-2 shadow-sm'>
                                <Card.Header>
                                    <div className='mx-4 d-flex justify-content-between'>
                                        <div><b>{change.userName}</b> changed a record</div>
                                        <small className='float-end'><b>{dayjs(change.changeDateTime).format('DD MMMM YYYY HH:mm:ss')}</b></small>
                                    </div>
                                </Card.Header>
                                <Card.Body className='mx-4 mb-3 p-2 bg-200 p-4 rounded'>
                                    <Row className='p-2'>
                                        <Col>
                                            <Badge bg='info'><FontAwesomeIcon icon={faBox} /> Stillage #{stillageNo}</Badge>
                                            {' '}
                                            <Badge bg='warning'>Field: <b className='text-uppercase'>{change.fieldName}</b></Badge>
                                        </Col>
                                    </Row>
                                    <Row className='p-2 d-flex align-items-center'>
                                        <Col xs={5}>
                                            <Badge bg='danger' className='w-100 shadow-sm text-decoration-line-through text-wrap'>{change.originalData || ''}</Badge>
                                        </Col>
                                        <Col xs={2} className='text-center fs-2'>
                                            <FontAwesomeIcon icon={faLongArrowAltRight} className='text-secondary' />
                                        </Col>
                                        <Col xs={5}>
                                            <Badge bg='success' className='w-100 shadow-sm text-wrap'>{change.newData || ''}</Badge>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    )
                })
            }
        </Row >
    );
}

StillageAudit.propTypes = {
    showModal: PropTypes.bool,
    setShowModal: PropTypes.func,
    stillageNo: PropTypes.number
};

export default memo(StillageAudit);